/** @jsx jsx */
import React from 'react';
import { css, styled, jsx } from '@compiled/react';
import Heading from '@atlaskit/heading';
import IssuesIcon from '@atlaskit/icon/core/migration/issues';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { fg } from '@atlassian/jira-feature-gating';
import HelpIcon from '../../../common/ui/help-icon/index.tsx';
import messages from './messages.tsx';

type Props = {
	issueCount: number;
	hasPermissionForAllIssues: boolean;
	deploymentDisplayName: string;
	deploymentUrl: string;
};

const DeploymentSummary = ({
	deploymentDisplayName,
	deploymentUrl,
	issueCount,
	hasPermissionForAllIssues,
}: Props) => {
	const { formatMessage } = useIntl();

	let issuesShippedMessage;
	if (hasPermissionForAllIssues) {
		issuesShippedMessage = fg('jira-issue-terminology-refresh-m3')
			? messages.issuesShippedIssueTermRefresh
			: messages.issuesShipped;
	} else {
		issuesShippedMessage = fg('jira-issue-terminology-refresh-m3')
			? messages.issuesShippedLimitedPermissionsIssueTermRefresh
			: messages.issuesShippedLimitedPermissions;
	}

	return (
		<Container>
			<Heading size="large">
				<DeploymentLink href={deploymentUrl} target="_blank">
					{deploymentDisplayName}
				</DeploymentLink>
			</Heading>
			<IssuesShippedContainer data-test-id="software-deployment-frequency-report-deployment-details-dialog.ui.body.deployment-summary.issues-shipped-container">
				<IssuesIcon LEGACY_size="small" label="" />
				{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
				<span css={marginLeftStyles}>
					{formatMessage(issuesShippedMessage, {
						issueCount,
					})}
				</span>
				{!hasPermissionForAllIssues && (
					<HelpIcon
						message={
							fg('jira-issue-terminology-refresh-m3')
								? formatMessage(messages.issuesShippedLimitedPermissionsTooltipIssueTermRefresh)
								: formatMessage(messages.issuesShippedLimitedPermissionsTooltip)
						}
						testId="software-deployment-frequency-report-deployment-details-dialog.ui.body.deployment-summary.help-icon"
						// @ts-expect-error Property 'css' does not exist on type 'IntrinsicAttributes & Props'.ts(2322)
						css={marginLeftStyles}
					/>
				)}
			</IssuesShippedContainer>
		</Container>
	);
};

export default DeploymentSummary;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	width: '100%',
	display: 'flex',
	flexDirection: 'column',
	paddingBottom: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DeploymentLink = styled.a({
	marginTop: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.link', colors.B400),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssuesShippedContainer = styled.div({
	paddingTop: token('space.100'),
	display: 'flex',
	alignItems: 'center',
});

const marginLeftStyles = css({
	marginLeft: token('space.050'),
});
